import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    JsonHubProtocol,
} from '@microsoft/signalr';
import Settings from 'settings/settings';
import { Logger } from '@utils/logger';

export interface IFrontEndMessageHub {
    connectToHub: (groupName: string) => Promise<void>;
    hubConnection: HubConnection;
    disconnectFromHub: () => void;
}

const frontEndMessageHub = (relativeEndpoint: string): IFrontEndMessageHub => {
    const hubUrl = `${Settings.api.frontendMessageHubUrl()}/${relativeEndpoint}`;

    const hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
            accessTokenFactory: () => sessionStorage?.getItem('token') ?? '',
        })
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: () => 5000,
        })
        .withHubProtocol(new JsonHubProtocol())
        .build();

    const connectToHub = async (groupName: string): Promise<void> => {
        if (hubConnection.state !== HubConnectionState.Connected) {
            try {
                await hubConnection.start();
            } catch (ex) {
                Logger.error(`Error when starting hub connection for hub: ${hubUrl}. Error: ${ex}`);
                throw ex;
            }
        }

        try {
            await hubConnection.invoke('RegisterUser', groupName);
            Logger.log(`Joined user to hub: ${hubUrl} for group: ${groupName}`);
        } catch (ex) {
            Logger.error(`Error on connecting to hub: ${hubUrl} for group: ${groupName}. Error: ${ex}`);
            throw ex;
        }
    };

    hubConnection.onreconnecting((error) => {
        Logger.log(`${new Date().toISOString()} Reconnecting to hub ${hubUrl}`, error);
    });

    hubConnection.onreconnected((connectionId) => {
        Logger.log(`${new Date().toISOString()} Reconnected to hub ${hubUrl}`, connectionId);
    });

    hubConnection.onclose((error) => {
        Logger.log(`${new Date().toISOString()} Connection to hub ${hubUrl} closed`, error);
    });

    const disconnectFromHub = () => {
        hubConnection.stop().catch((ex) => {
            Logger.error(`Error disconnecting from hub: ${hubUrl}. Error: ${ex}`);
        });
    };

    return { connectToHub, hubConnection, disconnectFromHub };
};

export default frontEndMessageHub;
